/**
 * Profile Page - Enhanced Version
 */
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Typography,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Chip,
  Skeleton,
  Fade,
  Slide,
  InputAdornment,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CameraAlt as CameraIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  VerifiedUser as VerifiedIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { ImageSizeHint } from '../../components/common';
import { IMAGE_SIZES } from '../../constants/imageSizes';
import { useGetProfileQuery, useUpdateProfileMutation } from '../../store/api/usersApi';
import { useAppDispatch } from '../../store/hooks';
import { updateUser } from '../../store/slices/authSlice';
import { palette } from '../../theme/palette';

interface FormErrors {
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  profile_picture?: string;
}

const ProfilePage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: profileData, isLoading, error } = useGetProfileQuery();
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    phone_number: '',
    first_name: '',
    last_name: '',
    gender: '' as 'male' | 'female' | '',
    date_of_birth: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update form data when profile loads
  useEffect(() => {
    if (profileData?.data) {
      const data = profileData.data;
      setFormData({
        phone_number: data.phone_number || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        gender: data.gender || '',
        date_of_birth: data.date_of_birth ? data.date_of_birth.split('T')[0] : '',
      });
      if (data.profile_picture) {
        setImagePreview(data.profile_picture);
      }
    }
  }, [profileData]);

  // Track changes
  useEffect(() => {
    if (!profileData?.data) return;

    const original = profileData.data;
    const hasFormChanges =
      formData.phone_number !== (original.phone_number || '') ||
      formData.first_name !== (original.first_name || '') ||
      formData.last_name !== (original.last_name || '') ||
      formData.gender !== (original.gender || '') ||
      formData.date_of_birth !== (original.date_of_birth ? original.date_of_birth.split('T')[0] : '');

    setHasChanges(hasFormChanges || !!profileImage);
  }, [formData, profileImage, profileData]);

  // Validation functions
  const validatePhoneNumber = (phone: string): string | undefined => {
    if (!phone) return undefined;
    const phoneRegex = /^(\+20|0)?1[0-9]{9}$/;
    if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
      return t('profile.form.phoneInvalid');
    }
    return undefined;
  };

  const validateDateOfBirth = (date: string): string | undefined => {
    if (!date) return undefined;
    const selectedDate = new Date(date);
    const today = new Date();
    const minDate = new Date('1900-01-01');

    if (selectedDate > today) {
      return t('profile.form.dobFuture');
    }
    if (selectedDate < minDate) {
      return t('profile.form.dateInvalid');
    }
    const age = today.getFullYear() - selectedDate.getFullYear();
    if (age > 120) {
      return t('profile.form.dobInvalid');
    }
    return undefined;
  };

  const validateImage = (file: File): string | undefined => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      return t('profile.form.imageInvalid');
    }
    if (file.size > maxSize) {
      return t('profile.form.imageTooLarge');
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (formData.phone_number) {
      const phoneError = validatePhoneNumber(formData.phone_number);
      if (phoneError) errors.phone_number = phoneError;
    }

    if (formData.date_of_birth) {
      const dateError = validateDateOfBirth(formData.date_of_birth);
      if (dateError) errors.date_of_birth = dateError;
    }

    if (profileImage) {
      const imageError = validateImage(profileImage);
      if (imageError) errors.profile_picture = imageError;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const error = validateImage(file);
      if (error) {
        setFormErrors((prev) => ({ ...prev, profile_picture: error }));
        return;
      }

      setProfileImage(file);
      setFormErrors((prev) => ({ ...prev, profile_picture: undefined }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = () => {
    if (isEditing) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    if (profileData?.data?.profile_picture) {
      setImagePreview(profileData.data.profile_picture);
    } else {
      setImagePreview(null);
    }
    setFormErrors((prev) => ({ ...prev, profile_picture: undefined }));
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return t('profile.notSet');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (!validateForm()) {
      setErrorMessage(t('profile.toast.fixErrors'));
      return;
    }

    try {
      const updateData: any = {};

      if (formData.phone_number) updateData.phone_number = formData.phone_number;
      if (formData.first_name) updateData.first_name = formData.first_name;
      if (formData.last_name) updateData.last_name = formData.last_name;
      if (formData.gender) updateData.gender = formData.gender;
      if (formData.date_of_birth) updateData.date_of_birth = formData.date_of_birth;
      if (profileImage) updateData.profile_picture = profileImage;

      const response = await updateProfile(updateData).unwrap();

      // Update auth state with new user data
      dispatch(updateUser({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        full_name: response.data.full_name || `${response.data.first_name || ''} ${response.data.last_name || ''}`.trim(),
        profile_picture: response.data.profile_picture || null,
      }));

      setSuccessMessage(response.message || t('profile.toast.updateSuccess'));
      setIsEditing(false);
      setProfileImage(null);
      setHasChanges(false);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      const errorMsg = err?.data?.message || err?.data?.detail || t('profile.toast.updateError');
      setErrorMessage(errorMsg);

      // Set field-specific errors if available
      if (err?.data) {
        const fieldErrors: FormErrors = {};
        Object.keys(err.data).forEach((key) => {
          if (Array.isArray(err.data[key])) {
            fieldErrors[key as keyof FormErrors] = err.data[key][0];
          } else if (typeof err.data[key] === 'string') {
            fieldErrors[key as keyof FormErrors] = err.data[key];
          }
        });
        if (Object.keys(fieldErrors).length > 0) {
          setFormErrors((prev) => ({ ...prev, ...fieldErrors }));
        }
      }
    }
  };

  const handleCancel = () => {
    if (profileData?.data) {
      const data = profileData.data;
      setFormData({
        phone_number: data.phone_number || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        gender: data.gender || '',
        date_of_birth: data.date_of_birth ? data.date_of_birth.split('T')[0] : '',
      });
      setImagePreview(data.profile_picture);
      setProfileImage(null);
      setFormErrors({});
      setHasChanges(false);
    }
    setIsEditing(false);
    setErrorMessage('');
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              {t('profile.buttons.retry')}
            </Button>
          }
        >
          {t('profile.toast.loadError')}
        </Alert>
      </Box>
    );
  }

  const profile = profileData?.data;
  const fullName = profile?.full_name ||
    `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() ||
    t('profile.defaultUser');

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {t('profile.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('profile.subtitle')}
          </Typography>
        </Box>
        {!isEditing && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setIsEditing(true)}
            sx={{
              background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
              '&:hover': {
                background: `linear-gradient(180deg, ${palette.brand[600]} 0%, ${palette.brandSecondary[600]} 100%)`,
              },
              boxShadow: `0 4px 12px ${alpha(palette.brand[500], 0.3)}`,
            }}
          >
            {t('profile.buttons.editProfile')}
          </Button>
        )}
      </Box>

      <Fade in={!!successMessage}>
        <Box>
          {successMessage && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{ mb: 3 }}
              action={
                <IconButton
                  aria-label={t('common.close')}
                  color="inherit"
                  size="small"
                  onClick={() => setSuccessMessage('')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {successMessage}
            </Alert>
          )}
        </Box>
      </Fade>

      <Fade in={!!errorMessage}>
        <Box>
          {errorMessage && (
            <Alert
              severity="error"
              icon={<ErrorIcon />}
              sx={{ mb: 3 }}
              action={
                <IconButton
                  aria-label={t('common.close')}
                  color="inherit"
                  size="small"
                  onClick={() => setErrorMessage('')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {errorMessage}
            </Alert>
          )}
        </Box>
      </Fade>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '1fr 2fr',
          },
          gap: 3,
        }}
      >
        {/* Profile Picture Card */}
        <Box>
          <Card
            sx={{
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(palette.brand[500], 0.05)} 0%, ${alpha(palette.brandSecondary[500], 0.05)} 100%)`,
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                <Avatar
                  src={imagePreview || undefined}
                  sx={{
                    width: 180,
                    height: 180,
                    fontSize: '4rem',
                    cursor: isEditing ? 'pointer' : 'default',
                    border: `4px solid ${palette.brand[500]}`,
                    boxShadow: `0 8px 24px ${alpha(palette.brand[500], 0.3)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': isEditing ? {
                      transform: 'scale(1.05)',
                      boxShadow: `0 12px 32px ${alpha(palette.brand[500], 0.4)}`,
                    } : {},
                  }}
                  onClick={handleImageClick}
                >
                  {profile?.first_name?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                {isEditing && (
                  <>
                    <Tooltip title={t('profile.tooltips.changePhoto')}>
                      <IconButton
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          backgroundColor: palette.brand[500],
                          color: 'white',
                          width: 48,
                          height: 48,
                          boxShadow: `0 4px 12px ${alpha(palette.brand[500], 0.4)}`,
                          '&:hover': {
                            backgroundColor: palette.brand[600],
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                        onClick={handleImageClick}
                      >
                        <CameraIcon />
                      </IconButton>
                    </Tooltip>
                    {imagePreview && (
                      <Tooltip title={t('profile.tooltips.removePhoto')}>
                        <IconButton
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: palette.status.danger,
                            color: 'white',
                            width: 36,
                            height: 36,
                            '&:hover': {
                              backgroundColor: palette.status.dangerDark,
                            },
                          }}
                          onClick={handleRemoveImage}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageChange}
                />
              </Box>

              {isEditing && (
                <ImageSizeHint
                  size={IMAGE_SIZES.profile}
                  sx={{ justifyContent: 'center', mt: 0, mb: 2 }}
                />
              )}

              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                {fullName}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {profile?.email}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                {profile?.is_superuser && (
                  <Chip
                    icon={<AdminIcon />}
                    label={t('profile.accountType.SUPER_ADMIN')}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}
                {profile?.is_verified && (
                  <Chip
                    icon={<VerifiedIcon />}
                    label={t('profile.status.verified')}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>

              {formErrors.profile_picture && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {formErrors.profile_picture}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Main Form Card */}
        <Box>
          <Card>
            <CardContent>
              <Box component="form" onSubmit={handleSubmit}>
                {/* Personal Information Section */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <PersonIcon sx={{ mr: 1, color: palette.brand[500] }} />
                    <Typography variant="h6" fontWeight={700}>
                      {t('profile.sections.personal')}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                      },
                      gap: 3,
                    }}
                  >
                    <Box>
                      <TextField
                        fullWidth
                        label={t('profile.form.email')}
                        value={profile?.email || ''}
                        disabled
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root.Mui-disabled': {
                            backgroundColor: alpha(palette.brand[500], 0.05),
                          },
                        }}
                      />
                    </Box>

                    <Box>
                      <TextField
                        fullWidth
                        label={t('profile.form.phoneNumber')}
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        variant="outlined"
                        placeholder="+201234567890"
                        error={!!formErrors.phone_number}
                        helperText={formErrors.phone_number}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon color={isEditing ? 'primary' : 'action'} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    <Box>
                      <TextField
                        fullWidth
                        label={t('profile.form.firstName')}
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        variant="outlined"
                        error={!!formErrors.first_name}
                        helperText={formErrors.first_name}
                      />
                    </Box>

                    <Box>
                      <TextField
                        fullWidth
                        label={t('profile.form.lastName')}
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        variant="outlined"
                        error={!!formErrors.last_name}
                        helperText={formErrors.last_name}
                      />
                    </Box>

                    <Box>
                      <TextField
                        fullWidth
                        select
                        label={t('profile.form.gender')}
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        variant="outlined"
                      >
                        <MenuItem value="">
                          <em>{t('profile.form.selectGender')}</em>
                        </MenuItem>
                        <MenuItem value="male">{t('profile.gender.male')}</MenuItem>
                        <MenuItem value="female">{t('profile.gender.female')}</MenuItem>
                      </TextField>
                    </Box>

                    <Box>
                      <TextField
                        fullWidth
                        label={t('profile.form.dateOfBirth')}
                        name="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        variant="outlined"
                        error={!!formErrors.date_of_birth}
                        helperText={formErrors.date_of_birth || t('profile.form.dobHelper')}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon color={isEditing ? 'primary' : 'action'} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Account Information Section */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <InfoIcon sx={{ mr: 1, color: palette.brand[500] }} />
                    <Typography variant="h6" fontWeight={700}>
                      {t('profile.sections.account')}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                      },
                      gap: 3,
                    }}
                  >
                    <Box>
                      <TextField
                        fullWidth
                        label={t('profile.form.accountType')}
                        value={
                          profile?.is_superuser
                            ? t('profile.accountType.SUPER_ADMIN')
                            : profile?.is_staff
                              ? t('profile.accountType.STAFF')
                              : t('profile.accountType.USER')
                        }
                        disabled
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AdminIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root.Mui-disabled': {
                            backgroundColor: alpha(palette.brand[500], 0.05),
                          },
                        }}
                      />
                    </Box>

                    <Box>
                      <TextField
                        fullWidth
                        label={t('profile.form.verificationStatus')}
                        value={profile?.is_verified ? t('profile.status.verified') : t('profile.status.notVerified')}
                        disabled
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VerifiedIcon
                                color={profile?.is_verified ? 'success' : 'action'}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root.Mui-disabled': {
                            backgroundColor: alpha(palette.brand[500], 0.05),
                          },
                        }}
                      />
                    </Box>

                    <Box>
                      <TextField
                        fullWidth
                        label={t('profile.form.dateJoined')}
                        value={formatDate(profile?.date_joined || null)}
                        disabled
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root.Mui-disabled': {
                            backgroundColor: alpha(palette.brand[500], 0.05),
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Action Buttons */}
                {isEditing && (
                  <Slide direction="up" in={isEditing} mountOnEnter unmountOnExit>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'flex-end',
                        mt: 4,
                        pt: 3,
                        borderTop: `1px solid ${alpha(palette.brand[500], 0.1)}`,
                      }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                        disabled={isUpdating}
                        size="large"
                        sx={{
                          minWidth: 140,
                          borderColor: palette.brand[500],
                          color: palette.brand[500],
                          '&:hover': {
                            borderColor: palette.brand[600],
                            backgroundColor: alpha(palette.brand[500], 0.05),
                          },
                        }}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={isUpdating ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        disabled={isUpdating || !hasChanges}
                        size="large"
                        sx={{
                          minWidth: 140,
                          background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
                          '&:hover': {
                            background: `linear-gradient(180deg, ${palette.brand[600]} 0%, ${palette.brandSecondary[600]} 100%)`,
                          },
                          boxShadow: `0 4px 12px ${alpha(palette.brand[500], 0.3)}`,
                          '&:disabled': {
                            background: alpha(palette.brand[500], 0.3),
                            boxShadow: 'none',
                          },
                        }}
                      >
                        {isUpdating ? t('profile.buttons.saving') : t('profile.buttons.saveChanges')}
                      </Button>
                    </Box>
                  </Slide>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfilePage;
